import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  SvgIcon,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from "@material-ui/core";
import React, { useEffect } from "react";
import {
  useGetReportCommentaryMutation,
  useUpdateReportCustomPromptMutation,
} from "~/store/mystore/amc.redux";

import ActionIconButton from "~/components/buttons/actionIconButton";
import AiInsightsIcon from "~/icons/aiInsightsIcon";
import AutoSizedButton from "~/components/buttons/autoSizedButton";
import CopyButton from "~/components/buttons/copyButton";
import { ErrorMessage } from "~/pages/marketplaceConnectors/amazonUpdateStoreNamePage";
import KeyboardArrowDownOutlinedIcon from "@material-ui/icons/KeyboardArrowDownOutlined";
import KeyboardArrowUpOutlinedIcon from "@material-ui/icons/KeyboardArrowUpOutlined";
import LoadingIndicator from "~/components/loadingIndicator/loadingIndicator";
import Panel from "~/components/panel/panel";
import RaisedButton from "~/components/buttons/raisedButton";
import ReactMarkdown from "react-markdown";
import RetryIcon from "~/icons/retryIcon";
import { isNil } from "lodash";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useTypedSelector } from "~/hooks/useTypedSelector";

interface AMCReportCommentaryProps {
  mid: string;
  reportRunId: string;
}

const AICommentaryWrapper = styled.div`
  padding: ${({ theme }) =>
    `${theme.spacing(2)}px
    ${theme.spacing(4)}px
    ${theme.spacing(4)}px
    ${theme.spacing(4)}px`};
  ${({ theme }) => `
    ${theme.breakpoints.down("sm")} {
      padding-left: ${theme.spacing(2)}px;
      padding-right: ${theme.spacing(2)}px;
    }
  `}
`;

const AICommentaryContent = styled.div<{ $showFullCommentary: boolean }>`
  line-height: 1.5;
  ${({ $showFullCommentary, theme }) =>
    !$showFullCommentary &&
    `
      max-height: 230px;
      overflow: hidden;
      position: relative;
      &:after {
        content: "";
        position: absolute;
        bottom: 0;
        left: 0;
        display: block;
        height: 50px;
        width: 100%;
        background: linear-gradient(to top,
          ${theme.palette.background.paper} 0%,
          ${alpha(theme.palette.background.paper, 0)} 100%
        );
      }
    `}

  & > *:first-child {
    margin-top: 0;
  }
  h2 {
    font-size: ${({ theme }) => theme.typography.h2.fontSize};
    font-weight: ${({ theme }) => theme.typography.h2.fontWeight};
    margin: 1em 0;
  }
  h3 {
    font-size: ${({ theme }) => theme.typography.h3.fontSize};
    font-weight: ${({ theme }) => theme.typography.h3.fontWeight};
    margin: 1em 0;
  }
  p {
    font-size: ${({ theme }) => theme.typography.body2.fontSize};
    font-weight: ${({ theme }) => theme.typography.body2.fontWeight};
    margin: 1em 0;
  }
  ul {
    list-style-type: disc;
    margin: 1em 0 1em 1.15em;
    li {
      font-size: ${({ theme }) => theme.typography.body2.fontSize};
      margin-bottom: 1em;
    }
  }
`;

const LoadingWrapper = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100px;
`;

const StyledAiInsightsIconWrapper = styled.div`
  & > svg {
    display: block;
    width: 20px;
    height: 20px;
    margin-right: 4px;
  }
`;

const StyledSvgIcon = styled(SvgIcon)`
  fill: ${({ theme }) => theme.palette.secondary.main};
`;

const StyledPromptChip = styled.div<{ $editPrompt: boolean }>`
  display: block;
  width: 12.5rem;
  border-radius: ${({ theme }) => theme.shape.borderRadius * 0.75}px;
  padding: 0.3rem 0.75rem;
  cursor: pointer;
  ${({ $editPrompt, theme }) =>
    $editPrompt
      ? `
        background-color: ${alpha(theme.palette.secondary.main, 0.2)};
        border: 1px solid ${theme.palette.secondary.main};
      `
      : `
        background-color: ${alpha(
          theme.darkModeEnabled
            ? theme.palette.grey[600]
            : theme.palette.grey[300],
          0.2
        )};
        &:hover {
          background-color: ${alpha(
            theme.darkModeEnabled
              ? theme.palette.grey[600]
              : theme.palette.grey[300],
            0.5
          )};
        }
      `}
`;

const MAX_COMMENTARY_INDEX = 2;

const AMCReportCommentary: React.FC<AMCReportCommentaryProps> = ({
  mid,
  reportRunId,
}) => {
  const { t } = useTranslation();

  const user = useTypedSelector((state) => state.user);

  const [refreshedCommentary, setRefreshedCommentary] = React.useState<
    string | null
  >(null);
  const [commentaryPrompt, setCommentaryPrompt] = React.useState<string>("");
  const [currentChoice, setCurrentChoice] = React.useState<
    number | undefined
  >();
  const [showFullCommentary, setShowFullCommentary] =
    React.useState<boolean>(false);
  const [editPrompt, setEditPrompt] = React.useState<boolean>(false);
  const [customPrompt, setCustomPrompt] = React.useState("");
  const [errorForCustomPrompt, setErrorForCustomPrompt] = React.useState(false);

  const [updateReportCustomPrompt, { isLoading }] =
    useUpdateReportCustomPromptMutation();

  const handleSave = async () => {
    const payload = {
      customPrompt,
      reportRunId,
      successMsg: t("amc.updateReportCustomPrompt.success"),
    };

    try {
      await updateReportCustomPrompt(payload);
      await fetchCommentary();
    } catch (error) {}
  };

  const [getReportCommentary, { isLoading: isFetching }] =
    useGetReportCommentaryMutation();

  const fetchCommentary = async () => {
    const { commentary, index, commentaryPrompt } = await getReportCommentary({
      mid,
      reportRunId,
      currentChoice,
    }).unwrap();
    commentary && setRefreshedCommentary(commentary);
    setCurrentChoice(index);
    setCommentaryPrompt(commentaryPrompt);
    setCustomPrompt(commentaryPrompt);
  };

  useEffect(() => {
    fetchCommentary();
  }, [getReportCommentary, mid, reportRunId]);

  return (
    <Panel
      id="widget-amc-report-commentary"
      title={t("amcWidget.commentary.title")}
      headerIcon={
        <StyledAiInsightsIconWrapper>
          <AiInsightsIcon />
        </StyledAiInsightsIconWrapper>
      }
      tooltip={t("amcWidget.commentary.tooltip")}
      titleGridSizeMd={2}
      actions={
        isNil(currentChoice) || isNil(refreshedCommentary) ? undefined : (
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Grid item xs={6}>
              {user.amcCustomPromptOn && (
                <Tooltip title={commentaryPrompt}>
                  <StyledPromptChip
                    $editPrompt={editPrompt}
                    onClick={() => {
                      setEditPrompt(!editPrompt);
                    }}
                  >
                    <>
                      <Typography
                        variant="body2"
                        color={editPrompt ? "secondary" : "textSecondary"}
                      >
                        {t("amc.Prompt")}
                      </Typography>
                      <Typography variant="body1" color="textPrimary">
                        {commentaryPrompt.slice(0, 30)}...
                      </Typography>
                    </>
                  </StyledPromptChip>
                </Tooltip>
              )}
            </Grid>
            <Grid
              container
              item
              justifyContent="flex-end"
              alignItems="center"
              xs={3}
            >
              <Grid item>
                <CopyButton
                  textToCopy={refreshedCommentary}
                  disabled={isFetching}
                />
              </Grid>
              {currentChoice < MAX_COMMENTARY_INDEX && (
                <Grid item>
                  <ActionIconButton
                    disabled={isFetching}
                    onClick={fetchCommentary}
                  >
                    <RetryIcon />
                  </ActionIconButton>
                </Grid>
              )}
              <Grid item>
                <Box mx={1}>
                  <Typography variant="body2" color="textSecondary">
                    {currentChoice + 1} of {MAX_COMMENTARY_INDEX + 1}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        )
      }
      content={
        user.amcCustomPromptOn && editPrompt ? (
          <AICommentaryWrapper>
            <Box p={2} maxWidth="50rem">
              <Grid
                container
                direction="column"
                justifyContent="flex-start"
                spacing={1}
              >
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <Typography variant="subtitle1" color="textSecondary">
                      {t("createReport.customPromptLabel")}
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      placeholder={t("createReport.customPromptPlaceholder")}
                      value={customPrompt}
                      multiline
                      minRows={4}
                      maxRows={15}
                      onChange={(e) => {
                        if (e.target.value.length > 1000) {
                          setErrorForCustomPrompt(true);
                        } else {
                          setErrorForCustomPrompt(false);
                          setCustomPrompt(e.target.value);
                        }
                      }}
                    />
                    {errorForCustomPrompt && (
                      <ErrorMessage>
                        <Typography variant="body1" color="error">
                          {t("createReport.customPromptErorMessage")}
                        </Typography>
                      </ErrorMessage>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Grid container justifyContent="flex-start" spacing={1}>
                    <Grid item>
                      <AutoSizedButton
                        disabled={isLoading || errorForCustomPrompt}
                        type="submit"
                        variant="contained"
                        color="secondary"
                        onClick={handleSave}
                      >
                        {isLoading ? (
                          <CircularProgress size={20} />
                        ) : (
                          t("createReport.customPromptSaveButton")
                        )}
                      </AutoSizedButton>
                    </Grid>
                    <Grid item>
                      <AutoSizedButton
                        variant="text"
                        onClick={() => {
                          setEditPrompt(!editPrompt);
                        }}
                        color="default"
                      >
                        {t("generic.closeButton").toUpperCase()}
                      </AutoSizedButton>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </AICommentaryWrapper>
        ) : isFetching ? (
          <LoadingWrapper>
            <LoadingIndicator size={40} />
          </LoadingWrapper>
        ) : refreshedCommentary?.length ? (
          <AICommentaryWrapper>
            <AICommentaryContent $showFullCommentary={showFullCommentary}>
              <ReactMarkdown>{refreshedCommentary}</ReactMarkdown>
            </AICommentaryContent>
            <RaisedButton
              variant="text"
              color="secondary"
              endIcon={
                <StyledSvgIcon
                  as={
                    showFullCommentary
                      ? KeyboardArrowUpOutlinedIcon
                      : KeyboardArrowDownOutlinedIcon
                  }
                />
              }
              onClick={() =>
                setShowFullCommentary(
                  (showFullCommentary) => !showFullCommentary
                )
              }
            >
              {showFullCommentary ? t("common.showLess") : t("common.showMore")}
            </RaisedButton>
          </AICommentaryWrapper>
        ) : (
          <AICommentaryWrapper>
            <Typography>{t("amcWidget.commentary.noDataMsg")}</Typography>
          </AICommentaryWrapper>
        )
      }
    />
  );
};

export default AMCReportCommentary;
